/**
 * 积分明细页：全量统计 + 类型筛选 + 分页流水。
 * 统计来自后端聚合，不再是当前页累加。
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import {
  api,
  type CreditStats,
  type CreditTransactionItem,
  type PaginationInfo,
} from '../../services/api';
import { EmptyState, Metric, SectionCard, TransactionRow } from './shared';

type Category = '' | 'spend' | 'earn' | 'refund';

const FILTERS: Array<{ key: Category; label: string }> = [
  { key: '', label: '全部' },
  { key: 'spend', label: '消费' },
  { key: 'earn', label: '获得' },
  { key: 'refund', label: '退款' },
];

const PAGE_SIZE = 20;

export default function CreditsPage() {
  const [stats, setStats] = useState<CreditStats | null>(null);
  const [transactions, setTransactions] = useState<CreditTransactionItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [category, setCategory] = useState<Category>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadPage = useCallback(
    async (page: number, nextCategory: Category) => {
      setLoading(true);
      try {
        const res = await api.getCreditTransactions({
          page,
          limit: PAGE_SIZE,
          category: nextCategory || undefined,
        });
        setTransactions(res.data ?? []);
        setPagination(res.pagination);
        setError('');
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载积分记录失败');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    api
      .getCredits()
      .then((res) => setStats(res.stats ?? null))
      .catch(() => setStats(null));
  }, []);

  useEffect(() => {
    void loadPage(1, category);
  }, [category, loadPage]);

  const changeCategory = (next: Category) => {
    if (next === category) return;
    setCategory(next);
  };

  return (
    <>
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      <section className="bg-white border border-light-border rounded-2xl shadow-sm p-5">
        <div className="grid grid-cols-3 gap-3">
          <Metric label="累计获得" value={`+${stats?.totalEarned ?? 0}`} tone="earn" />
          <Metric label="累计消费" value={`-${stats?.totalSpent ?? 0}`} tone="spend" />
          <Metric label="本月消费" value={`-${stats?.monthSpent ?? 0}`} />
        </div>
      </section>

      <SectionCard
        title="积分流水"
        subtitle={`共 ${pagination.total} 条记录`}
        action={
          <div className="flex gap-1.5">
            {FILTERS.map((item) => (
              <button
                key={item.key || 'all'}
                onClick={() => changeCategory(item.key)}
                className={`px-2.5 py-1 rounded-lg text-xs transition-colors ${
                  category === item.key
                    ? 'bg-accent-blue text-white'
                    : 'text-light-secondary hover:bg-light-hover'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        }
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-accent-blue" />
          </div>
        ) : transactions.length === 0 ? (
          <EmptyState text="暂无积分记录" />
        ) : (
          <div className="divide-y divide-light-border">
            {transactions.map((tx) => (
              <TransactionRow key={tx.id} tx={tx} />
            ))}
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="px-5 py-3 border-t border-light-border flex items-center justify-between">
            <span className="text-xs text-light-muted">
              第 {pagination.page} / {pagination.totalPages} 页
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => void loadPage(pagination.page - 1, category)}
                disabled={pagination.page <= 1 || loading}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-light-border text-light-secondary hover:bg-light-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                上一页
              </button>
              <button
                onClick={() => void loadPage(pagination.page + 1, category)}
                disabled={pagination.page >= pagination.totalPages || loading}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-light-border text-light-secondary hover:bg-light-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                下一页
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </SectionCard>
    </>
  );
}
